/**
 * YT Availability Matrix – Drawer overlay showing color×size availability grid.
 * Opens from the configurator, allows clicking a cell to select color+size.
 */
if (!customElements.get('yt-availability-matrix')) {
  class YTAvailabilityMatrix extends HTMLElement {
    constructor() {
      super();
      this._onKeydown = this._onKeydown.bind(this);
      this._product = null;
      this._state = null;
      this._translations = {};
      this._wheelsFilter = null;
    }

    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-matrix-close]')) {
          this.close();
          return;
        }
        const cell = e.target.closest('[data-matrix-color][data-matrix-size]');
        if (cell && !cell.classList.contains('yt-availability-matrix__td--unavailable')) {
          this.onCellClick(cell.dataset.matrixColor, cell.dataset.matrixSize);
        }
        const filterBtn = e.target.closest('[data-matrix-wheels]');
        if (filterBtn) {
          this._wheelsFilter = filterBtn.dataset.matrixWheels;
          this.querySelectorAll('[data-matrix-wheels]').forEach((b) => {
            b.classList.toggle('is-active', b.dataset.matrixWheels === this._wheelsFilter);
          });
          this.renderMatrix();
        }
      });

      /* Keyboard activation for matrix cells (WCAG 2.1.1).
         Cells render with role="button" tabindex="0" — Enter/Space mirrors click. */
      this.addEventListener('keydown', (e) => {
        const cell = e.target.closest('[data-matrix-color][data-matrix-size][role="button"]');
        if (!cell) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.onCellClick(cell.dataset.matrixColor, cell.dataset.matrixSize);
        }
      });
    }

    open(product, state, translations) {
      this._product = product;
      this._state = { ...state };
      this._translations = translations || {};
      this._wheelsFilter = state.wheels || null;
      this._triggerElement = document.activeElement;

      this.buildContent();
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this._onKeydown);

      // Focus the close button
      const closeBtn = this.querySelector('.yt-availability-matrix__close');
      if (closeBtn) closeBtn.focus();
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this._onKeydown);
      if (this._triggerElement) this._triggerElement.focus();
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      /* Focus trap: keep Tab navigation within the open drawer (WCAG 2.1.2). */
      if (e.key === 'Tab' && this.classList.contains('is-open')) {
        const focusables = this._getFocusables();
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    }

    _getFocusables() {
      const sel = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), [role="button"][tabindex="0"]';
      return Array.from(this.querySelectorAll(sel)).filter((el) => {
        return !el.hasAttribute('disabled') && el.offsetParent !== null;
      });
    }

    buildContent() {
      const content = this.querySelector('[data-matrix-content]');
      if (!content) return;
      content.innerHTML = '';

      const product = this._product;
      const options = product.options || [];
      const colorOpt = options.find((o) => ['color', 'colour', 'farbe'].includes(o.name.toLowerCase()));
      const sizeOpt = options.find((o) => ['size', 'größe'].includes(o.name.toLowerCase()));
      const wheelsOpt = options.find((o) => o.name.toLowerCase().includes('wheel') || o.name.toLowerCase().includes('laufrad'));

      if (!colorOpt || !sizeOpt) {
        content.textContent = 'No color/size options available.';
        return;
      }

      // Wheels filter if product has wheel option
      if (wheelsOpt && wheelsOpt.values.length > 1) {
        // Default to first wheel value if none selected
        if (!this._wheelsFilter) this._wheelsFilter = wheelsOpt.values[0];

        const filter = document.createElement('div');
        filter.className = 'yt-availability-matrix__filter';

        wheelsOpt.values.forEach((w) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'yt-availability-matrix__filter-btn';
          btn.dataset.matrixWheels = w;
          btn.textContent = w;
          if (w === this._wheelsFilter) btn.classList.add('is-active');
          filter.appendChild(btn);
        });

        content.appendChild(filter);
      }

      // Grid container for the matrix
      this._gridContainer = document.createElement('div');
      content.appendChild(this._gridContainer);
      this.renderMatrix();
    }

    renderMatrix() {
      if (!this._gridContainer) return;
      this._gridContainer.innerHTML = '';

      const product = this._product;
      const options = product.options || [];
      const colorOpt = options.find((o) => ['color', 'colour', 'farbe'].includes(o.name.toLowerCase()));
      const sizeOpt = options.find((o) => ['size', 'größe'].includes(o.name.toLowerCase()));
      const wheelsOpt = options.find((o) => o.name.toLowerCase().includes('wheel') || o.name.toLowerCase().includes('laufrad'));

      const colors = this.normalizeValues(colorOpt.values);
      const sizes = this.normalizeValues(sizeOpt.values).map((v) => v.name);
      const colorKey = `option${colorOpt.position}`;
      const sizeKey = `option${sizeOpt.position}`;
      const wheelsKey = wheelsOpt ? `option${wheelsOpt.position}` : null;

      const labels = {
        available: this._translations.in_stock || 'On Stock',
        soldOut: this._translations.out_of_stock || 'Sold Out',
      };

      const table = document.createElement('table');
      table.className = 'yt-availability-matrix__table';

      // Header row: empty corner + color swatch + name
      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');
      const cornerTh = document.createElement('th');
      cornerTh.className = 'yt-availability-matrix__th yt-availability-matrix__th--corner';
      headerRow.appendChild(cornerTh);
      colors.forEach((color) => {
        const th = document.createElement('th');
        th.className = 'yt-availability-matrix__th';
        const swatchColor = color.swatch || this.guessSwatchColor(product, colorKey, color.name);
        th.innerHTML =
          '<span class="yt-availability-matrix__color-head">' +
            '<span class="yt-availability-matrix__swatch"' + (swatchColor ? ' style="background-color:' + swatchColor + ';"' : '') + '></span>' +
            '<span class="yt-availability-matrix__color-name">' + this.escapeHtml(color.name) + '</span>' +
          '</span>';
        headerRow.appendChild(th);
      });
      thead.appendChild(headerRow);
      table.appendChild(thead);

      // Data rows
      const tbody = document.createElement('tbody');
      sizes.forEach((size) => {
        const tr = document.createElement('tr');

        const sizeTd = document.createElement('td');
        sizeTd.className = 'yt-availability-matrix__size';
        sizeTd.textContent = size;
        tr.appendChild(sizeTd);

        colors.forEach((color) => {
          const td = document.createElement('td');
          td.className = 'yt-availability-matrix__td';
          td.dataset.matrixColor = color.name;
          td.dataset.matrixSize = size;

          const matching = product.variants.filter((v) => {
            if (v[colorKey] !== color.name) return false;
            if (v[sizeKey] !== size) return false;
            if (wheelsKey && this._wheelsFilter && v[wheelsKey] !== this._wheelsFilter) return false;
            return true;
          });

          const available = matching.some((v) => v.available);
          const shipDate = matching.find((v) => v.ship_date)?.ship_date || null;

          const statusEl = document.createElement('span');
          statusEl.className = 'yt-availability-matrix__status';

          let stateLabel = '';
          if (shipDate) {
            td.classList.add('yt-availability-matrix__td--shipping');
            statusEl.classList.add('yt-availability-matrix__status--shipping');
            statusEl.textContent = shipDate;
            stateLabel = shipDate;
          } else if (available) {
            td.classList.add('yt-availability-matrix__td--available');
            statusEl.classList.add('yt-availability-matrix__status--available');
            statusEl.textContent = labels.available;
            stateLabel = labels.available;
          } else {
            td.classList.add('yt-availability-matrix__td--unavailable');
            statusEl.classList.add('yt-availability-matrix__status--sold-out');
            statusEl.textContent = labels.soldOut;
            stateLabel = labels.soldOut;
          }

          td.appendChild(statusEl);

          if (this._state.color === color.name && this._state.size === size) {
            td.classList.add('yt-availability-matrix__td--current');
          }

          /* Make available cells keyboard-operable (WCAG 2.1.1).
             Unavailable cells stay non-interactive. */
          if (available) {
            td.setAttribute('role', 'button');
            td.setAttribute('tabindex', '0');
            td.setAttribute('aria-label', `${color.name}, ${size}: ${stateLabel}`);
          } else {
            td.setAttribute('aria-label', `${color.name}, ${size}: ${stateLabel}`);
          }

          tr.appendChild(td);
        });

        tbody.appendChild(tr);
      });
      table.appendChild(tbody);

      this._gridContainer.appendChild(table);
    }

    /**
     * Normalize option values: strings become {name, swatch: null}, objects stay as-is.
     * Shopify 2024 may return objects with { name, swatch: { color, image } }.
     */
    normalizeValues(values) {
      return values.map((v) => {
        if (typeof v === 'string') return { name: v, swatch: null };
        if (v && typeof v === 'object') {
          return {
            name: v.name || v.title || String(v),
            swatch: (v.swatch && (v.swatch.color || v.swatch.image)) || null,
          };
        }
        return { name: String(v), swatch: null };
      });
    }

    /**
     * Try to derive a swatch color from the first variant's featured image.
     * Fallback uses a generic color map for common YT colors, else null (drawer shows an empty bordered square).
     */
    guessSwatchColor(product, colorKey, colorName) {
      const map = {
        'fog green': '#8a9a83',
        'black magic bolt': '#1a1a1a',
        'black': '#1a1a1a',
        'stealth black': '#0d0d0d',
        'white': '#ececec',
        'anthracite': '#3a3a3a',
        'red': '#c4645c',
        'blue': '#3a5f8f',
        'orange': '#d97b3f',
        'silver': '#b8b8b8',
      };
      return map[String(colorName || '').toLowerCase()] || null;
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    onCellClick(color, size) {
      this.dispatchEvent(new CustomEvent('matrix-select', {
        bubbles: true,
        detail: { color, size },
      }));
      this.close();
    }
  }

  customElements.define('yt-availability-matrix', YTAvailabilityMatrix);
}
